import { httpRequest } from '@/utils/axios';
import { getFirstPageAdpParam } from './marketingPageAdp';
import type {
  MarketingBrandProductListResponse,
  MarketingCouponsReceiveResponse,
  MarketingCouponsResponse,
  MarketingFlashSaleProductListResponse,
  MarketingProductListResponse,
  MarketingRankingProductListResponse,
  MarketingSceneProductListResponse,
  SeedingItemsResponse,
  SeedingItemDetailResponse,
} from '@/app/(activity)/campaigns/[slug]/types';

const SEEDING_ITEMS_URL = 'GET:/api/marketing_pages/seeding/items' as const;
const SEEDING_ITEM_DETAIL_URL = 'GET:/api/marketing_pages/seeding/items' as const;
const MARKETING_PRODUCT_LIST_URL = 'GET:/api/marketing_pages/product_list' as const;
const MARKETING_COUPONS_URL = 'GET:/api/marketing_pages/coupons' as const;
const MARKETING_COUPONS_RECEIVE_URL = 'POST:/api/marketing_pages/coupons/receive' as const;

interface MarketingCouponsParams {
  marketingPageId: number;
  isPreview?: boolean;
}

interface ReceiveMarketingCouponsParams {
  marketingPageId: number;
  couponIds: Array<number | string>;
}

export const fetchMarketingCoupons = ({
  marketingPageId,
  isPreview = false,
}: MarketingCouponsParams): Promise<MarketingCouponsResponse> => {
  return httpRequest(MARKETING_COUPONS_URL as any, {
    params: {
      marketing_page_id: marketingPageId,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const receiveMarketingCoupons = ({
  marketingPageId,
  couponIds,
}: ReceiveMarketingCouponsParams): Promise<MarketingCouponsReceiveResponse> => {
  const couponPayload =
    couponIds.length === 1 ? { coupon_id: couponIds[0] } : { coupon_ids: couponIds };

  return httpRequest(MARKETING_COUPONS_RECEIVE_URL as any, {
    data: {
      marketing_page_id: marketingPageId,
      ...couponPayload,
    },
  }).then((res) => (res as any).data || res);
};

/**
 * 获取种草坑位列表（客户端）
 * @param marketingPageId 页面ID
 * @param isPreview 是否预览模式
 * @param page 页码
 * @param pageSize 每页条数
 */
export const fetchSeedingItems = (
  marketingPageId: number,
  isPreview: boolean = false,
  page: number = 1,
  pageSize: number = 20,
  adp?: string,
): Promise<SeedingItemsResponse> => {
  return httpRequest(SEEDING_ITEMS_URL as any, {
    params: {
      marketing_page_id: marketingPageId,
      page,
      page_size: pageSize,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

/**
 * 获取种草坑位详情（客户端）
 * @param itemId 坑位ID
 * @param marketingPageId 页面ID
 * @param isPreview 是否预览模式
 */
export const fetchSeedingItemDetail = (
  itemId: number,
  marketingPageId: number,
  isPreview: boolean = false,
): Promise<SeedingItemDetailResponse> => {
  return httpRequest(`${SEEDING_ITEM_DETAIL_URL}/${itemId}` as any, {
    params: {
      marketing_page_id: marketingPageId,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

interface MarketingProductListParams {
  groupId: number;
  page?: number;
  pageSize?: number;
  pageId: number;
  isPreview?: boolean;
  adp?: string;
  in_stock?: number;
}

interface MarketingFlashSaleProductListParams {
  newUserRefId: number;
  oldUserRefId: number;
  page?: number;
  pageSize?: number;
  pageId: number;
  isPreview?: boolean;
  adp?: string;
}

interface MarketingStyleCategoryProductListParams {
  pageId: number;
  itemIndex: number;
  page?: number;
  pageSize?: number;
  adp?: string;
  isPreview?: boolean;
  appendCorrelation?: number;
}

interface MarketingBrandProductListParams {
  marketingPageId: number;
  isPreview?: boolean;
  page?: number;
  pageSize?: number;
  adp?: string;
}

interface MarketingSceneProductListParams {
  marketingPageId: number;
  isPreview?: boolean;
  itemIndex: number;
  page?: number;
  pageSize?: number;
  adp?: string;
}

interface MarketingRankingProductListParams {
  marketingPageId: number;
  isPreview?: boolean;
  itemIndex: number;
  page?: number;
  pageSize?: number;
}

export const fetchMarketingProductList = ({
  groupId,
  page = 1,
  pageSize = 20,
  pageId,
  isPreview = false,
  adp,
  in_stock,
}: MarketingProductListParams): Promise<MarketingProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'product_group',
      ref_id: groupId,
      page,
      page_size: pageSize,
      marketing_page_id: pageId,
      ...(in_stock !== undefined ? { in_stock } : {}),
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingStyleCategoryProductList = ({
  pageId,
  itemIndex,
  page = 1,
  pageSize = 50,
  adp,
  isPreview = false,
  appendCorrelation = 1,
}: MarketingStyleCategoryProductListParams): Promise<MarketingProductListResponse> => {
  return httpRequest(
    MARKETING_PRODUCT_LIST_URL as any,
    {
      params: {
        type: 'style_category_product',
        marketing_page_id: pageId,
        item_index: itemIndex,
        page,
        page_size: pageSize,
        append_correlation: appendCorrelation,
        ...(isPreview ? { preview: 1 } : {}),
        ...getFirstPageAdpParam(page, adp),
      },
      data: {},
    },
    { success: false, fail: false },
  ).then((res) => (res as any).data || res);
};

export const fetchMarketingBrandProductList = ({
  marketingPageId,
  isPreview = false,
  page = 1,
  pageSize = 8,
  adp,
}: MarketingBrandProductListParams): Promise<MarketingBrandProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'brand_product',
      marketing_page_id: marketingPageId,
      page,
      page_size: pageSize,
      append_correlation: 1,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingSceneProductList = ({
  marketingPageId,
  isPreview = false,
  itemIndex,
  page = 1,
  pageSize = 20,
  adp,
}: MarketingSceneProductListParams): Promise<MarketingSceneProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'scene_product',
      marketing_page_id: marketingPageId,
      item_index: itemIndex,
      page,
      page_size: pageSize,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingRankingProductList = ({
  marketingPageId,
  isPreview = false,
  itemIndex,
  page = 1,
  pageSize = 18,
}: MarketingRankingProductListParams): Promise<MarketingRankingProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'ranking_product',
      marketing_page_id: marketingPageId,
      item_index: itemIndex,
      page,
      page_size: pageSize,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingFlashSaleProductList = ({
  newUserRefId,
  oldUserRefId,
  page = 1,
  pageSize = 8,
  pageId,
  isPreview = false,
  adp,
}: MarketingFlashSaleProductListParams): Promise<MarketingProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'flash_sale',
      new_user_ref_id: newUserRefId,
      old_user_ref_id: oldUserRefId,
      page,
      page_size: pageSize,
      marketing_page_id: pageId,
      ...(isPreview ? { preview: 1 } : {}),
      ...getFirstPageAdpParam(page, adp),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};

export const fetchMarketingMainFlashSaleProductList = (
  marketingPageId: number,
  isPreview: boolean = false,
): Promise<MarketingFlashSaleProductListResponse> => {
  return httpRequest(MARKETING_PRODUCT_LIST_URL as any, {
    params: {
      type: 'flash_sale',
      marketing_page_id: marketingPageId,
      ...(isPreview ? { preview: 1 } : {}),
    },
    data: {},
  }).then((res) => (res as any).data || res);
};
